import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Save, Loader2, CheckCircle, Play, AlertCircle,
  Clock, ScanEye, PenLine, BellRing, Cpu, CalendarDays,
} from 'lucide-react';
import { plannerAPI } from '../services/api';
import toast from 'react-hot-toast';

function Section({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50">
        <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
          <Icon size={16}/>
        </div>
        <div>
          <p className="text-sm font-bold text-slate-800">{title}</p>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function NumberInput({ value, onChange, min, max, unit }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(parseInt(e.target.value) || min)}
        className="w-20 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400"
      />
      {unit && <span className="text-xs text-slate-400 whitespace-nowrap">{unit}</span>}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer
          ${checked ? 'bg-primary-600' : 'bg-slate-200'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200
          ${checked ? 'translate-x-5' : 'translate-x-0'}`}/>
      </div>
      {label && <span className="text-sm text-slate-600">{label}</span>}
    </label>
  );
}

const DAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function LessonPlannerSettings({ yearId }) {
  const [cfg,      setCfg]      = useState(null);
  const [form,     setForm]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [dirty,    setDirty]    = useState(false);
  const [saved,    setSaved]    = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    plannerAPI.getConfig({ academic_year_id: yearId || 3 })
      .then(r => {
        setCfg(r.data);
        setForm(r.data);
        setDirty(false);
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await plannerAPI.updateConfig({ ...form, academic_year_id: yearId || 3 });
      setCfg(r.data);
      setForm(r.data);
      setDirty(false);
      setSaved(true);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const triggerSla = async () => {
    if (!window.confirm('Run the SLA watchdog now? This will check for overdue reviews and approvals and insert breach notifications.')) return;
    setTriggering(true);
    try {
      await plannerAPI.triggerSla();
      toast.success('SLA watchdog ran successfully — check Notifications for any new breach alerts');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to run SLA watchdog');
    } finally {
      setTriggering(false);
    }
  };

  const reset = () => {
    setForm(cfg);
    setDirty(false);
    setSaved(false);
  };

  if (loading) return (
    <div className="py-16 flex items-center justify-center text-slate-400 text-sm gap-3">
      <Loader2 size={18} className="animate-spin"/> Loading settings…
    </div>
  );

  if (!form) return null;

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Saved banner */}
      {saved && !dirty && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle size={15}/> Settings saved successfully
        </div>
      )}

      {/* 1. Plan submission deadline */}
      <Section icon={CalendarDays} title="Plan Submission Deadline"
        description="The day of the week by which teachers must submit their lesson plans">
        <FieldRow label="Submission deadline day"
          hint="Teachers who submit after this day will be flagged as late">
          <select
            value={form.plan_submission_deadline_day}
            onChange={e => set('plan_submission_deadline_day', parseInt(e.target.value))}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400">
            {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </FieldRow>
      </Section>

      {/* 2. SLA timers */}
      <Section icon={Clock} title="Review & Approval SLA"
        description="Maximum time allowed at each workflow stage before a breach notification is generated">
        <FieldRow label="Coordinator review SLA"
          hint="Hours allowed for coordinator to review after a plan is submitted">
          <NumberInput value={form.coordinator_review_sla_hrs} min={1} max={168}
            onChange={v => set('coordinator_review_sla_hrs', v)} unit="hours"/>
        </FieldRow>
        <FieldRow label="VP approval SLA"
          hint="Hours allowed for VP to approve after coordinator approves">
          <NumberInput value={form.vp_approval_sla_hrs} min={1} max={168}
            onChange={v => set('vp_approval_sla_hrs', v)} unit="hours"/>
        </FieldRow>
      </Section>

      {/* 3. Observation settings */}
      <Section icon={ScanEye} title="Observation Settings"
        description="How often teachers should be observed in the classroom each term">
        <FieldRow label="Observations per teacher per term"
          hint="Minimum number of classroom observations a coordinator should conduct">
          <NumberInput value={form.observation_frequency_per_term} min={1} max={10}
            onChange={v => set('observation_frequency_per_term', v)} unit="observations"/>
        </FieldRow>
      </Section>

      {/* 4. Reflection deadline */}
      <Section icon={PenLine} title="Reflection Deadline"
        description="How many days after the lesson a teacher must submit their reflection">
        <FieldRow label="Reflection deadline"
          hint="Days after lesson date before reflection is marked overdue">
          <NumberInput value={form.reflection_deadline_days} min={1} max={14}
            onChange={v => set('reflection_deadline_days', v)} unit="days"/>
        </FieldRow>
      </Section>

      {/* 5. Feature toggles */}
      <Section icon={Cpu} title="Feature Toggles"
        description="Enable or disable optional features for this academic year">
        <FieldRow label="AI plan quality analysis"
          hint="Automatically score lesson plans using Claude AI when approved">
          <Toggle checked={!!form.ai_analysis_enabled}
            onChange={v => set('ai_analysis_enabled', v ? 1 : 0)}/>
        </FieldRow>
        <FieldRow label="Telegram breach alerts"
          hint="Send Telegram messages to coordinators and VP when SLA is breached">
          <Toggle checked={!!form.telegram_alerts_enabled}
            onChange={v => set('telegram_alerts_enabled', v ? 1 : 0)}/>
        </FieldRow>
      </Section>

      {/* 6. Manual triggers */}
      <Section icon={BellRing} title="Manual Triggers"
        description="Run background jobs on demand instead of waiting for the scheduled time">
        <FieldRow label="Run SLA watchdog now"
          hint="Checks all pending plans for SLA breaches and inserts breach notifications immediately">
          <button onClick={triggerSla} disabled={triggering}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 disabled:opacity-60 transition-colors">
            {triggering ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>}
            {triggering ? 'Running…' : 'Run Now'}
          </button>
        </FieldRow>
      </Section>

      {/* Save / Reset bar */}
      <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl sticky bottom-4 shadow-lg">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          {dirty
            ? <><AlertCircle size={12} className="text-amber-500"/> Unsaved changes</>
            : cfg?.updated_at
              ? <><CheckCircle size={12} className="text-green-500"/> Last saved {new Date(cfg.updated_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</>
              : null
          }
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={reset}
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
              Reset
            </button>
          )}
          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
